import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTournament } from '../context/TournamentContext';
import * as api from '../lib/api';
import type { Team, Player, MatchUp, Game } from '../data/types';
import { Shield, Users, UserPlus, Swords, Plus, Trash2, Edit, Save, X, Camera, KeyRound, Clock, Lock, Unlock } from 'lucide-react';
import { PasswordInput } from '../components/PasswordInput';

import { useNavigate } from 'react-router-dom';

// ── Time slot helper: 08:00 → 18:00 in 10-min steps ──
function generateTimeSlots(): string[] {
  const slots: string[] = [];
  for (let h = 8; h <= 18; h++) {
    for (let m = 0; m < 60; m += 10) {
      if (h === 18 && m > 0) break;
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return slots;
}
const TIME_SLOTS = generateTimeSlots();


export function AdminPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const { tournament, teams, players, matchUps, refreshAll, getTeam, getGamesByMatchUp, getPlayersByTeam } = useTournament();
  const [activeTab, setActiveTab] = useState<'teams' | 'players' | 'bracket'>('bracket');

  if (!isAdmin) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon"><Shield size={48} /></div>
        <p className="mb-md">Přístup pouze pro administrátory</p>
        <button className="btn btn-accent" onClick={() => navigate('/login')}>
          Přihlásit se
        </button>
      </div>
    );
  }

  return (
    <div>
      <h1 className="mb-md">
        <Shield size={24} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
        Admin
      </h1>

      <div className="admin-tabs">
        <button className={`admin-tab ${activeTab === 'teams' ? 'active' : ''}`} onClick={() => setActiveTab('teams')}>
          <Users size={16} style={{ verticalAlign: 'middle', marginRight: '0.375rem' }} />
          Týmy
        </button>
        <button className={`admin-tab ${activeTab === 'players' ? 'active' : ''}`} onClick={() => setActiveTab('players')}>
          <UserPlus size={16} style={{ verticalAlign: 'middle', marginRight: '0.375rem' }} />
          Hráči
        </button>
        <button className={`admin-tab ${activeTab === 'bracket' ? 'active' : ''}`} onClick={() => setActiveTab('bracket')}>
          <Swords size={16} style={{ verticalAlign: 'middle', marginRight: '0.375rem' }} />
          Bracket & Hry
        </button>
      </div>

      {activeTab === 'teams' && <TeamsTab tournament={tournament} teams={teams} refreshAll={refreshAll} />}
      {activeTab === 'players' && <PlayersTab tournament={tournament} teams={teams} players={players} refreshAll={refreshAll} />}
      {activeTab === 'bracket' && <BracketTab matchUps={matchUps} teams={teams} getTeam={getTeam} getGamesByMatchUp={getGamesByMatchUp} getPlayersByTeam={getPlayersByTeam} refreshAll={refreshAll} />}
    </div>
  );
}

// ===== TEAMS TAB =====
function TeamsTab({ tournament, teams, refreshAll }: {
  tournament: any; teams: Team[]; refreshAll: () => Promise<void>;
}) {
  const [newName, setNewName] = useState('');
  const [newGroup, setNewGroup] = useState<'A' | 'B'>('A');
  const [newSeed, setNewSeed] = useState(1);
  const [newColor, setNewColor] = useState('#5B3A9E');
  const [newLogoFile, setNewLogoFile] = useState<File | null>(null);
  const [newLogoPreview, setNewLogoPreview] = useState<string | null>(null);
  const newFileInputRef = useRef<HTMLInputElement>(null);

  // Edit state
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editGroup, setEditGroup] = useState<'A' | 'B'>('A');
  const [editSeed, setEditSeed] = useState(1);
  const [editColor, setEditColor] = useState('#5B3A9E');
  const [editLogoFile, setEditLogoFile] = useState<File | null>(null);
  const [editLogoPreview, setEditLogoPreview] = useState<string | null>(null);
  const editFileInputRef = useRef<HTMLInputElement>(null);

  function handleNewLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setNewLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setNewLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  }

  function handleEditLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setEditLogoFile(file);
    const reader = new FileReader();
    reader.onload = () => setEditLogoPreview(reader.result as string);
    reader.readAsDataURL(file);
  }

  async function handleAdd() {
    if (!newName.trim() || !tournament) return;
    const team = await api.createTeam({
      tournament_id: tournament.id,
      name: newName.trim(),
      group_name: newGroup,
      seed: newSeed,
      color: newColor,
    });
    if (team && newLogoFile) {
       await api.uploadTeamLogo(team.id, newLogoFile);
    }
    setNewName('');
    setNewLogoFile(null);
    setNewLogoPreview(null);
    await refreshAll();
  }

  async function handleDelete(id: string) {
    if (!confirm('Smazat tým?')) return;
    await api.deleteTeam(id);
    await refreshAll();
  }

  function startEdit(team: Team) {
    setEditingTeamId(team.id);
    setEditName(team.name);
    setEditGroup((team.group_name as 'A' | 'B') ?? 'A');
    setEditSeed(team.seed ?? 1);
    setEditColor(team.color ?? '#5B3A9E');
    setEditLogoFile(null);
    setEditLogoPreview(team.logo_url ?? null);
  }

  function cancelEdit() {
    setEditingTeamId(null);
    setEditLogoFile(null);
    setEditLogoPreview(null);
  }

  async function handleSaveEdit() {
    if (!editingTeamId || !editName.trim()) return;
    await api.updateTeam(editingTeamId, {
      name: editName.trim(),
      group_name: editGroup,
      seed: editSeed,
      color: editColor,
    });
    if (editLogoFile) {
      await api.uploadTeamLogo(editingTeamId, editLogoFile);
    }
    setEditingTeamId(null);
    setEditLogoFile(null);
    setEditLogoPreview(null);
    await refreshAll();
  }

  const groupA = teams.filter(t => t.group_name === 'A').sort((a, b) => (a.seed ?? 0) - (b.seed ?? 0));
  const groupB = teams.filter(t => t.group_name === 'B').sort((a, b) => (a.seed ?? 0) - (b.seed ?? 0));

  function renderTeamRow(t: Team) {
    const isEditing = editingTeamId === t.id;

    if (isEditing) {
      return (
        <div key={t.id} className="admin-item mb-sm" style={{ padding: '0.75rem', flexDirection: 'column', alignItems: 'stretch', gap: '0.75rem' }}>
          <div className="flex gap-md" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
            
            {/* Team Logo Editor */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
              <div
                onClick={() => editFileInputRef.current?.click()}
                style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: editLogoPreview ? `url(${editLogoPreview}) center/cover no-repeat` : 'var(--bg-surface)',
                  border: '2px dashed var(--border)',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden',
                  transition: 'border-color 0.2s',
                }}
                title="Nahrát logo"
              >
                {!editLogoPreview && <Camera size={16} style={{ color: 'var(--text-muted)' }} />}
              </div>
              <input ref={editFileInputRef} type="file" accept="image/*" onChange={handleEditLogoChange} style={{ display: 'none' }} />
            </div>

            <div className="flex gap-sm" style={{ flexWrap: 'wrap', flex: 1, alignItems: 'center' }}>
              <div style={{ flex: 1, minWidth: '120px' }}>
                <label style={{ fontSize: '0.6875rem' }}>Název</label>
                <input className="input" value={editName} onChange={e => setEditName(e.target.value)} style={{ fontSize: '0.8125rem' }} />
              </div>
              <div>
                <label style={{ fontSize: '0.6875rem' }}>Skupina</label>
                <select className="input" value={editGroup} onChange={e => setEditGroup(e.target.value as 'A' | 'B')} style={{ fontSize: '0.8125rem' }}>
                  <option value="A">A</option>
                  <option value="B">B</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.6875rem' }}>Seed</label>
                <select className="input" value={editSeed} onChange={e => setEditSeed(Number(e.target.value))} style={{ fontSize: '0.8125rem' }}>
                  {[1,2,3,4].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.6875rem' }}>Barva</label>
                <input type="color" value={editColor} onChange={e => setEditColor(e.target.value)} style={{ height: '34px', width: '42px', cursor: 'pointer', padding: 0, border: 'none' }} />
              </div>
            </div>
            
            <div className="flex gap-xs" style={{ alignItems: 'center', marginTop: '1rem' }}>
              <button className="btn btn-accent btn-sm" onClick={handleSaveEdit} title="Uložit">
                <Save size={14} />
              </button>
              <button className="btn btn-ghost btn-sm" onClick={cancelEdit} title="Zrušit">
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={t.id} className="admin-item mb-sm" style={{ alignItems: 'center' }}>
        <div className="admin-item-info flex items-center gap-sm">
          <div style={{
            width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
            background: t.logo_url ? `url(${t.logo_url}) center/cover no-repeat` : 'var(--bg-surface)',
            border: `2px solid ${t.color}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
             {!t.logo_url && <span style={{ width: 8, height: 8, borderRadius: '50%', background: t.color }} />}
          </div>
          <strong style={{ fontSize: '0.9375rem' }}>{t.name}</strong>
          <span className="text-muted" style={{ fontSize: '0.8125rem' }}>(Seed {t.seed})</span>
        </div>
        <div className="flex gap-xs" style={{ alignItems: 'center' }}>
          <button className="btn btn-ghost btn-sm" onClick={() => startEdit(t)} title="Upravit">
            <Edit size={14} />
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(t.id)} title="Smazat">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Add team form */}
      <div className="card mb-lg">
        <h4 className="mb-md">Přidat tým</h4>
        <div className="flex gap-md" style={{ flexWrap: 'wrap', alignItems: 'center' }}>
          
          {/* Logo Upload */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
            <div
              onClick={() => newFileInputRef.current?.click()}
              style={{
                width: 56, height: 56, borderRadius: '50%',
                background: newLogoPreview ? `url(${newLogoPreview}) center/cover no-repeat` : 'var(--bg-surface)',
                border: '2px dashed var(--border)',
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
                transition: 'border-color 0.2s',
              }}
              title="Nahrát logo"
            >
              {!newLogoPreview && <Camera size={20} style={{ color: 'var(--text-muted)' }} />}
            </div>
            <input ref={newFileInputRef} type="file" accept="image/*" onChange={handleNewLogoChange} style={{ display: 'none' }} />
            <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>Avatar</span>
          </div>

          <div className="flex gap-sm" style={{ flexWrap: 'wrap', flex: 1, alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: '150px' }}>
              <label>Název</label>
              <input className="input" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Název univerzity" />
            </div>
            <div>
              <label>Skupina</label>
              <select className="input" value={newGroup} onChange={e => setNewGroup(e.target.value as 'A' | 'B')}>
                <option value="A">A</option>
                <option value="B">B</option>
              </select>
            </div>
            <div>
              <label>Seed</label>
              <select className="input" value={newSeed} onChange={e => setNewSeed(Number(e.target.value))}>
                {[1,2,3,4].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label>Barva</label>
              <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)} style={{ height: '38px', width: '50px', cursor: 'pointer', padding: 0, border: 'none' }} />
            </div>
          </div>
          
          <button className="btn btn-accent" onClick={handleAdd} style={{ marginTop: '1.25rem' }}>
            <Plus size={16} /> Přidat
          </button>
        </div>
      </div>

      {/* Team lists */}
      <div className="grid-2">
        <div className="card">
          <h4 className="mb-md">Skupina A</h4>
          {groupA.map(t => renderTeamRow(t))}
          {groupA.length === 0 && <p className="text-muted">Žádné týmy</p>}
        </div>

        <div className="card">
          <h4 className="mb-md">Skupina B</h4>
          {groupB.map(t => renderTeamRow(t))}
          {groupB.length === 0 && <p className="text-muted">Žádné týmy</p>}
        </div>
      </div>
    </div>
  );
}

// ===== PLAYERS TAB =====
function PlayersTab({ tournament, teams, players, refreshAll }: {
  tournament: any; teams: Team[]; players: Player[]; refreshAll: () => Promise<void>;
}) {
  const [newName, setNewName] = useState('');
  const [newTeamId, setNewTeamId] = useState('');
  const [createAuth, setCreateAuth] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Edit state
  const [editingPlayerId, setEditingPlayerId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editTeamId, setEditTeamId] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleAdd() {
    if (!newName.trim() || !newTeamId || !tournament) return;

    if (createAuth) {
      // Generate email and password from name, removing diacritics
      const sanitize = (str: string) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const cleanName = sanitize(newName.trim().toLowerCase());
      const parts = cleanName.split(' ');
      
      const email = parts.length >= 2
        ? `${parts[0]}.${parts[parts.length-1]}@unimatchplay.golf`
        : `${parts[0]}@unimatchplay.golf`;
      const password = parts.length >= 2
        ? `${parts[parts.length-1]}_67`
        : `${parts[0]}_67`;

      await api.createPlayerWithAuth(
        { tournament_id: tournament.id, name: newName.trim(), team_id: newTeamId },
        email,
        password
      );
    } else {
      await api.createPlayer({
        tournament_id: tournament.id,
        name: newName.trim(),
        team_id: newTeamId,
      });
    }

    setNewName('');
    await refreshAll();
  }

  async function handleDelete(id: string) {
    if (!confirm('Smazat hráče?')) return;
    await api.deletePlayer(id);
    await refreshAll();
  }

  function startEdit(player: Player) {
    setEditingPlayerId(player.id);
    setEditName(player.name);
    setEditTeamId(player.team_id ?? '');
    setEditPassword('');
    setAvatarPreview(player.avatar_url ?? null);
    setAvatarFile(null);
  }

  function cancelEdit() {
    setEditingPlayerId(null);
    setAvatarPreview(null);
    setAvatarFile(null);
    setEditPassword('');
  }

  function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    // Create preview URL
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);
  }

  async function handleSaveEdit() {
    if (!editingPlayerId || !editName.trim()) return;
    setSaving(true);
    try {
      // Update player basic info
      await api.updatePlayer(editingPlayerId, {
        name: editName.trim(),
        team_id: editTeamId || null,
      });

      // Upload avatar if changed
      if (avatarFile) {
        await api.uploadAvatar(editingPlayerId, avatarFile);
      }

      // Update password if provided
      const player = players.find(p => p.id === editingPlayerId);
      if (editPassword.trim() && player?.auth_id) {
        const success = await api.updatePlayerPassword(player.auth_id, editPassword.trim());
        if (!success) {
          alert('Nepodařilo se změnit heslo');
        }
      }

      setEditingPlayerId(null);
      setAvatarPreview(null);
      setAvatarFile(null);
      setEditPassword('');
      await refreshAll();
    } finally {
      setSaving(false);
    }
  }

  function renderPlayerRow(p: Player) {
    const isEditing = editingPlayerId === p.id;

    if (isEditing) {
      return (
        <div key={p.id} className="admin-item mb-sm" style={{ flexDirection: 'column', alignItems: 'stretch', gap: '0.75rem', padding: '1rem' }}>
          <div className="flex gap-md" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
            {/* Avatar */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.375rem' }}>
              <div
                onClick={() => fileInputRef.current?.click()}
                style={{
                  width: 64, height: 64, borderRadius: '50%',
                  background: avatarPreview ? `url(${avatarPreview}) center/cover no-repeat` : 'var(--bg-surface)',
                  border: '2px dashed var(--border)',
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden',
                  transition: 'border-color 0.2s',
                }}
                title="Nahrát avatar"
              >
                {!avatarPreview && <Camera size={20} style={{ color: 'var(--text-muted)' }} />}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                style={{ display: 'none' }}
              />
              <span className="text-muted" style={{ fontSize: '0.625rem' }}>Avatar</span>
            </div>

            {/* Fields */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '0.5rem', minWidth: '200px' }}>
              <div className="flex gap-sm" style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div style={{ flex: 1, minWidth: '150px' }}>
                  <label style={{ fontSize: '0.6875rem' }}>Jméno</label>
                  <input className="input" value={editName} onChange={e => setEditName(e.target.value)} style={{ fontSize: '0.8125rem' }} />
                </div>
                <div style={{ minWidth: '120px' }}>
                  <label style={{ fontSize: '0.6875rem' }}>Tým</label>
                  <select className="input" value={editTeamId} onChange={e => setEditTeamId(e.target.value)} style={{ fontSize: '0.8125rem' }}>
                    <option value="">-</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Password field — only show if player has auth account */}
              {p.auth_id && (
                <div style={{ maxWidth: '300px' }}>
                  <label style={{ fontSize: '0.6875rem' }}>
                    <KeyRound size={10} style={{ verticalAlign: 'middle', marginRight: '0.25rem' }} />
                    Nové heslo
                  </label>
                  <PasswordInput
                    className="input"
                    value={editPassword}
                    onChange={e => setEditPassword(e.target.value)}
                    placeholder="Ponechte prázdné = beze změny"
                    style={{ fontSize: '0.8125rem' }}
                  />
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-xs" style={{ alignSelf: 'flex-start', paddingTop: '1.125rem' }}>
              <button className="btn btn-accent btn-sm" onClick={handleSaveEdit} disabled={saving} title="Uložit">
                <Save size={14} />
              </button>
              <button className="btn btn-ghost btn-sm" onClick={cancelEdit} disabled={saving} title="Zrušit">
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div key={p.id} className="admin-item mb-sm">
        <div className="admin-item-info" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
          {/* Avatar thumbnail */}
          <div style={{
            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
            background: p.avatar_url ? `url(${p.avatar_url}) center/cover no-repeat` : 'var(--bg-surface)',
            border: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {!p.avatar_url && <Users size={12} style={{ color: 'var(--text-muted)' }} />}
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <strong style={{ display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</strong>
            {p.email && <span className="text-muted" style={{ fontSize: '0.6875rem', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.email}</span>}
          </div>
        </div>
        <div className="flex gap-xs" style={{ flexShrink: 0 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => startEdit(p)} title="Upravit">
            <Edit size={14} />
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => handleDelete(p.id)} title="Smazat">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Add player */}
      <div className="card mb-lg">
        <h4 className="mb-md" style={{ textAlign: 'center' }}>Přidat hráče</h4>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxWidth: '400px', margin: '0 auto' }}>
          <div className="form-group">
            <label>Jméno</label>
            <input className="input" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Jméno Příjmení" />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Tým</label>
              <select className="input" value={newTeamId} onChange={e => setNewTeamId(e.target.value)}>
                <option value="">Vyberte tým</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-sm" style={{ paddingBottom: '0.5rem', flexShrink: 0 }}>
              <input type="checkbox" id="create-auth" checked={createAuth} onChange={e => setCreateAuth(e.target.checked)} />
              <label htmlFor="create-auth" style={{ marginBottom: 0, whiteSpace: 'nowrap' }}>Vytvořit login</label>
            </div>
          </div>
          <button className="btn btn-accent" onClick={handleAdd} style={{ alignSelf: 'center' }}>
            <Plus size={16} /> Přidat
          </button>
        </div>
      </div>

      {/* Search players */}
      <div className="mb-lg">
        <div style={{ position: 'relative' }}>
          <input
            className="input"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="🔍  Hledat hráče podle jména nebo e-mailu..."
            style={{ width: '100%', fontSize: '0.875rem', padding: '0.75rem 1rem' }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer',
                fontSize: '1rem', lineHeight: 1, padding: '0.25rem',
              }}
              title="Vymazat"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Players by team */}
      <div className="flex flex-col gap-md">
        {teams.map(team => {
          const sq = searchQuery.toLowerCase().trim();
          const teamPlayers = players
            .filter(p => p.team_id === team.id)
            .filter(p => !sq || p.name.toLowerCase().includes(sq) || (p.email?.toLowerCase().includes(sq)));
          if (sq && teamPlayers.length === 0) return null;
          return (
            <div key={team.id} className="card">
              <div className="flex items-center gap-sm mb-md">
                <span style={{ width: 12, height: 12, borderRadius: '50%', background: team.color }} />
                <h4>{team.name}</h4>
                <span className="badge badge-scheduled">{teamPlayers.length} hráčů</span>
              </div>
              {teamPlayers.map(p => renderPlayerRow(p))}
              {teamPlayers.length === 0 && <p className="text-muted">Žádní hráči</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===== BRACKET TAB =====
function BracketTab({ matchUps, teams, getTeam, getGamesByMatchUp, getPlayersByTeam, refreshAll }: {
  matchUps: MatchUp[];
  teams: Team[];
  getTeam: (id: string) => Team | undefined;
  getGamesByMatchUp: (id: string) => Game[];
  getPlayersByTeam: (id: string) => Player[];
  refreshAll: () => Promise<void>;
}) {
  const [editingMatchUp, setEditingMatchUp] = useState<string | null>(null);
  const [teamAId, setTeamAId] = useState('');
  const [teamBId, setTeamBId] = useState('');

  async function handleSaveMatchUp(id: string) {
    await api.updateMatchUp(id, { team_a_id: teamAId || null, team_b_id: teamBId || null } as any);
    setEditingMatchUp(null);
    await refreshAll();
  }

  async function handleCreateGame(matchUpId: string, type: 'single' | 'foursome', gameNumber: number) {
    await api.createGame({
      match_up_id: matchUpId,
      type,
      game_number: gameNumber,
    });
    await refreshAll();
  }

  async function handleDeleteGame(gameId: string) {
    if (!confirm('Smazat hru?')) return;
    await api.deleteGame(gameId);
    await refreshAll();
  }

  async function handleAssignPlayer(gameId: string, field: string, playerId: string) {
    await api.updateGame(gameId, { [field]: playerId || null } as any);
    await refreshAll();
  }

  async function handleToggleLock(mu: MatchUp) {
    await api.updateMatchUp(mu.id, { locked: !mu.locked } as any);
    await refreshAll();
  }

  async function handleUpdateMatchUpStatus(id: string, status: string) {
    await api.updateMatchUp(id, { status } as any);
    await refreshAll();
  }

  return (
    <div className="flex flex-col gap-md">
      {matchUps.map(mu => {
        const isEditing = editingMatchUp === mu.id;
        const muGames = getGamesByMatchUp(mu.id);
        const teamA = mu.team_a_id ? getTeam(mu.team_a_id) : null;
        const teamB = mu.team_b_id ? getTeam(mu.team_b_id) : null;

        return (
          <div key={mu.id} className="card">
            {/* Match-up header */}
            <div style={{ marginBottom: '1rem' }}>
              <div className="flex items-center justify-between" style={{ marginBottom: '0.35rem' }}>
                <h4 style={{ fontSize: '1rem' }}>{mu.label}</h4>
                <div className="flex items-center gap-xs" style={{ flexShrink: 0 }}>
                  <Clock size={10} style={{ color: 'var(--text-muted)' }} />
                  <select
                    className="input"
                    value={mu.scheduled_time ?? ''}
                    onChange={async (e) => {
                      await api.updateMatchUp(mu.id, { scheduled_time: e.target.value || null } as any);
                      await refreshAll();
                    }}
                    style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', width: 'auto', padding: '0.2rem 0.4rem', height: 'auto' }}
                  >
                    <option value="">— čas —</option>
                    {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-xs" style={{ flexWrap: 'wrap', gap: '0.375rem' }}>
                <span className="text-muted" style={{ fontSize: '0.75rem', marginRight: '0.25rem' }}>{mu.round}</span>
                <select
                  className="input"
                  value={mu.status}
                  onChange={(e) => handleUpdateMatchUpStatus(mu.id, e.target.value)}
                  style={{ width: 'auto', fontSize: '0.75rem', padding: '0.25rem 0.5rem', height: 'auto' }}
                >
                  <option value="scheduled">Nezačal</option>
                  <option value="in_progress">Live</option>
                  <option value="completed">Hotovo</option>
                </select>
                {!isEditing ? (
                  <button className="btn btn-outline btn-sm" style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }} onClick={() => {
                    setEditingMatchUp(mu.id);
                    setTeamAId(mu.team_a_id ?? '');
                    setTeamBId(mu.team_b_id ?? '');
                  }}>
                    <Edit size={12} /> Týmy
                  </button>
                ) : (
                  <>
                    <button className="btn btn-accent btn-sm" style={{ padding: '0.25rem 0.5rem' }} onClick={() => handleSaveMatchUp(mu.id)}>
                      <Save size={12} />
                    </button>
                    <button className="btn btn-ghost btn-sm" style={{ padding: '0.25rem 0.5rem' }} onClick={() => setEditingMatchUp(null)}>
                      <X size={12} />
                    </button>
                  </>
                )}
                <button
                  className={`btn btn-sm ${mu.locked ? 'btn-danger' : 'btn-outline'}`}
                  onClick={() => handleToggleLock(mu)}
                  title={mu.locked ? 'Odemknout výsledky' : 'Zamknout výsledky'}
                  style={{
                    fontSize: '0.75rem', padding: '0.25rem 0.5rem',
                    ...(mu.locked ? { background: 'var(--error)', color: 'white', borderColor: 'var(--error)' } : {}),
                  }}
                >
                  {mu.locked ? <><Lock size={12} /> 🔒</> : <><Unlock size={12} /> 🔓</>}
                </button>
              </div>
            </div>

            {/* Team assignment */}
            {isEditing ? (
              <div className="flex gap-md mb-md" style={{ flexWrap: 'wrap' }}>
                <div>
                  <label>Tým A</label>
                  <select className="input" value={teamAId} onChange={e => setTeamAId(e.target.value)}>
                    <option value="">TBD</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <label>Tým B</label>
                  <select className="input" value={teamBId} onChange={e => setTeamBId(e.target.value)}>
                    <option value="">TBD</option>
                    {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-sm mb-md" style={{ fontSize: '0.9375rem' }}>
                <span style={{ color: teamA?.color, fontWeight: 700 }}>{teamA?.name ?? 'TBD'}</span>
                <span className="text-muted">vs</span>
                <span style={{ color: teamB?.color, fontWeight: 700 }}>{teamB?.name ?? 'TBD'}</span>
              </div>
            )}

            {/* Games */}
            <div className="flex flex-col gap-sm">
              {muGames.map(game => {
                const label = game.type === 'foursome' ? 'Foursome' : `Single ${game.game_number}`;
                // Get available players from both teams
                const teamAPlayers = mu.team_a_id ? getPlayersByTeam(mu.team_a_id) : [];
                const teamBPlayers = mu.team_b_id ? getPlayersByTeam(mu.team_b_id) : [];

                return (
                  <div key={game.id} style={{ padding: '0.75rem', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)' }}>
                    <div className="flex items-center justify-between mb-sm">
                      <strong style={{ fontSize: '0.8125rem' }}>{label}</strong>
                      <div className="flex items-center gap-sm">
                        {/* Tee time input */}
                        <div className="flex items-center gap-xs">
                          <Clock size={11} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                          <select
                            className="input"
                            value={game.tee_time ?? ''}
                            onChange={async (e) => {
                              await api.updateGame(game.id, { tee_time: e.target.value || null } as any);
                              await refreshAll();
                            }}
                            style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', width: 'auto', padding: '0.2rem 0.4rem', height: 'auto' }}
                          >
                            <option value="">— čas —</option>
                            {TIME_SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <button className="btn btn-ghost btn-sm" onClick={() => handleDeleteGame(game.id)}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>

                    {game.type === 'single' ? (
                      <div className="flex gap-md" style={{ flexWrap: 'wrap' }}>
                        <div>
                          <label style={{ fontSize: '0.6875rem' }}>Hráč A</label>
                          <select className="input" style={{ fontSize: '0.8125rem' }}
                            value={game.player_a_id ?? ''} onChange={e => handleAssignPlayer(game.id, 'player_a_id', e.target.value)}>
                            <option value="">Vybrat</option>
                            {teamAPlayers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.6875rem' }}>Hráč B</label>
                          <select className="input" style={{ fontSize: '0.8125rem' }}
                            value={game.player_b_id ?? ''} onChange={e => handleAssignPlayer(game.id, 'player_b_id', e.target.value)}>
                            <option value="">Vybrat</option>
                            {teamBPlayers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-sm" style={{ flexWrap: 'wrap' }}>
                        <div>
                          <label style={{ fontSize: '0.6875rem' }}>Tým A - Hráč 1</label>
                          <select className="input" style={{ fontSize: '0.8125rem' }}
                            value={game.foursome_a1_id ?? ''} onChange={e => handleAssignPlayer(game.id, 'foursome_a1_id', e.target.value)}>
                            <option value="">Vybrat</option>
                            {teamAPlayers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.6875rem' }}>Tým A - Hráč 2</label>
                          <select className="input" style={{ fontSize: '0.8125rem' }}
                            value={game.foursome_a2_id ?? ''} onChange={e => handleAssignPlayer(game.id, 'foursome_a2_id', e.target.value)}>
                            <option value="">Vybrat</option>
                            {teamAPlayers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.6875rem' }}>Tým B - Hráč 1</label>
                          <select className="input" style={{ fontSize: '0.8125rem' }}
                            value={game.foursome_b1_id ?? ''} onChange={e => handleAssignPlayer(game.id, 'foursome_b1_id', e.target.value)}>
                            <option value="">Vybrat</option>
                            {teamBPlayers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ fontSize: '0.6875rem' }}>Tým B - Hráč 2</label>
                          <select className="input" style={{ fontSize: '0.8125rem' }}
                            value={game.foursome_b2_id ?? ''} onChange={e => handleAssignPlayer(game.id, 'foursome_b2_id', e.target.value)}>
                            <option value="">Vybrat</option>
                            {teamBPlayers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Create games buttons */}
              {muGames.length < 3 && (
                <div className="flex gap-sm mt-sm">
                  {!muGames.find(g => g.game_number === 1) && (
                    <button className="btn btn-outline btn-sm" onClick={() => handleCreateGame(mu.id, 'single', 1)}>
                      <Plus size={14} /> Single 1
                    </button>
                  )}
                  {!muGames.find(g => g.game_number === 2) && (
                    <button className="btn btn-outline btn-sm" onClick={() => handleCreateGame(mu.id, 'single', 2)}>
                      <Plus size={14} /> Single 2
                    </button>
                  )}
                  {!muGames.find(g => g.game_number === 3) && (
                    <button className="btn btn-outline btn-sm" onClick={() => handleCreateGame(mu.id, 'foursome', 3)}>
                      <Plus size={14} /> Foursome
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
